import { DebounceSettings, debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * 로컬 상태와 디바운스된 업데이트를 함께 관리하는 Hook
 *
 * @template T - 상태 값의 타입
 * @param initialValue - 초기값
 * @param onUpdate - 업데이트 콜백 함수 (디바운스됨)
 * @param delay - 디바운스 지연 시간 (ms)
 * @param options - lodash debounce 옵션
 * @returns [현재값, 값 변경 함수, 취소 함수]
 *
 * @example
 * ```tsx
 * const [description, setDescription] = useDebouncedUpdate(
 *   issueCaseDoc.description ?? '',
 *   async (value) => {
 *     await updateIssueCase(id, { description: value });
 *   },
 *   1000
 * );
 *
 * <Input
 *   value={description}
 *   onChange={(e) => setDescription(e.target.value)}
 * />
 * ```
 */
function useDebouncedUpdate<T>(
  initialValue: T,
  onUpdate: (value: T) => void | Promise<void>,
  delay: number,
  options?: DebounceSettings
): [T, (value: T) => void, () => void] {
  const [value, setValue] = useState<T>(initialValue);
  const onUpdateRef = useRef(onUpdate);

  // 외부에서 initialValue가 변경되면 로컬 상태도 업데이트
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // 최신 콜백을 ref에 저장
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // 디바운스된 업데이트 함수 생성
  const debouncedUpdate = useMemo(() => {
    const func = (newValue: T) => onUpdateRef.current(newValue);
    return debounce(func, delay, options);
  }, [delay, options]);

  // 컴포넌트 언마운트 시 pending된 디바운스 함수 취소
  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  // 값 변경 핸들러
  const handleChange = useCallback(
    (newValue: T) => {
      setValue(newValue);
      debouncedUpdate(newValue);
    },
    [debouncedUpdate]
  );

  // 수동 취소 함수
  const cancel = useCallback(() => {
    debouncedUpdate.cancel();
  }, [debouncedUpdate]);

  return [value, handleChange, cancel];
}

export default useDebouncedUpdate;
